"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.LimitDisclosureEvaluationHandler = void 0;
const pex_models_1 = require("@sphereon/pex-models");
const ConstraintUtils_1 = require("../../ConstraintUtils");
const Messages_1 = __importDefault(require("../../types/Messages"));
const utils_1 = require("../../utils");
const abstractEvaluationHandler_1 = require("./abstractEvaluationHandler");
class LimitDisclosureEvaluationHandler extends abstractEvaluationHandler_1.AbstractEvaluationHandler {
    constructor(client) {
        super(client);
    }
    getName() {
        return 'LimitDisclosureEvaluation';
    }
    handle(pd, wrappedVcs) {
        // PresentationDefinitionV2 is the common denominator
        pd.input_descriptors.forEach((inDesc, index) => {
            var _a, _b, _c;
            if (((_a = inDesc.constraints) === null || _a === void 0 ? void 0 : _a.fields) &&
                (((_b = inDesc.constraints) === null || _b === void 0 ? void 0 : _b.limit_disclosure) === pex_models_1.Optionality.Required ||
                    ((_c = inDesc.constraints) === null || _c === void 0 ? void 0 : _c.limit_disclosure) === pex_models_1.Optionality.Preferred)) {
                this.evaluateLimitDisclosure(wrappedVcs, inDesc.constraints, index);
            }
        });
    }
    isLimitDisclosureSupported(wvc, vcIdx, idIdx, optionality) {
        const limitDisclosureSignatures = this.client.limitDisclosureSignatureSuites;
        const proof = wvc.decoded.proof;
        if (!proof || Array.isArray(proof) || !proof.type) {
            // todo: Support/inspect array based proofs
            return false;
        }
        else if (!(limitDisclosureSignatures === null || limitDisclosureSignatures === void 0 ? void 0 : limitDisclosureSignatures.includes(proof.type))) {
            if (optionality == pex_models_1.Optionality.Required) {
                this.createLimitDisclosureNotSupportedResult(idIdx, vcIdx);
            }
            return false;
        }
        return true;
    }
    evaluateLimitDisclosure(wrappedVcs, constraints, idIdx) {
        const fields = constraints === null || constraints === void 0 ? void 0 : constraints.fields;
        const optionality = constraints.limit_disclosure;
        wrappedVcs.forEach((wvc, index) => {
            if (optionality && this.isLimitDisclosureSupported(wvc, index, idIdx, optionality)) {
                this.enforceLimitDisclosure(wvc.internalCredential, fields, idIdx, index, wrappedVcs, optionality);
            }
        });
    }
    enforceLimitDisclosure(vc, fields, idIdx, index, wrappedVcs, limitDisclosure) {
        const internalCredentialToSend = this.createVcWithRequiredFields(vc, fields, idIdx, index);
        /* When verifiableCredentialToSend is null/undefined an error is raised, the credential will
         * remain untouched and the verifiable credential won't be submitted.
         */
        if (internalCredentialToSend) {
            wrappedVcs[index].internalCredential = internalCredentialToSend;
            this.createSuccessResult(idIdx, `$[${index}]`, limitDisclosure);
        }
    }
    createVcWithRequiredFields(vc, fields, idIdx, vcIdx) {
        let credentialToSend = {};
        credentialToSend = Object.assign(credentialToSend, vc);
        credentialToSend.credentialSubject = {};
        for (const field of fields) {
            if (field.path) {
                const inputField = utils_1.JsonPathUtils.extractInputField(vc, field.path);
                if (inputField.length > 0) {
                    credentialToSend = this.copyResultPathToDestinationCredential(inputField[0], vc, credentialToSend);
                }
                else {
                    this.createMandatoryFieldNotFoundResult(idIdx, vcIdx, field.path);
                    return undefined;
                }
            }
        }
        return credentialToSend;
    }
    copyResultPathToDestinationCredential(requiredField, internalCredential, internalCredentialToSend) {
        let credentialSubject = Object.assign({}, internalCredential.credentialSubject);
        requiredField.path.forEach((e) => {
            if (credentialSubject[e]) {
                credentialSubject = { [e]: credentialSubject[e] };
            }
        });
        internalCredentialToSend.credentialSubject = Object.assign(Object.assign({}, internalCredentialToSend.credentialSubject), credentialSubject);
        return internalCredentialToSend;
    }
    createSuccessResult(idIdx, path, limitDisclosure) {
        return this.getResults().push({
            input_descriptor_path: `$.input_descriptors[${idIdx}]`,
            verifiable_credential_path: `${path}`,
            evaluator: this.getName(),
            status: limitDisclosure === pex_models_1.Optionality.Required ? ConstraintUtils_1.Status.INFO : ConstraintUtils_1.Status.WARN,
            message: Messages_1.default.LIMIT_DISCLOSURE_APPLIED,
            payload: undefined,
        });
    }
    createMandatoryFieldNotFoundResult(idIdx, vcIdx, path) {
        return this.getResults().push({
            input_descriptor_path: `$.input_descriptors[${idIdx}]`,
            verifiable_credential_path: `$[${vcIdx}]`,
            evaluator: this.getName(),
            status: ConstraintUtils_1.Status.ERROR,
            message: Messages_1.default.VERIFIABLE_CREDENTIAL_MANDATORY_FIELD_NOT_PRESENT,
            payload: path,
        });
    }
    createLimitDisclosureNotSupportedResult(idIdx, vcIdx) {
        return this.getResults().push({
            input_descriptor_path: `$.input_descriptors[${idIdx}]`,
            verifiable_credential_path: `$[${vcIdx}]`,
            evaluator: this.getName(),
            status: ConstraintUtils_1.Status.ERROR,
            message: Messages_1.default.LIMIT_DISCLOSURE_NOT_SUPPORTED,
        });
    }
}
exports.LimitDisclosureEvaluationHandler = LimitDisclosureEvaluationHandler;
